import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { DeltaCell } from './AllocationPreviewTable';
import type { PreviewRow } from './types';

export interface ReviewBatchGroup {
  batchLabel: string;
  rows: PreviewRow[];
}

interface ReviewImpactTableProps {
  groups: ReviewBatchGroup[];
}

/** Combined impact table across all selected batches, grouped by batch (row-spanned). */
export const ReviewImpactTable = ({ groups }: ReviewImpactTableProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  return (
    <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden' }}>
      <Table size="medium">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theme.palette.grey[50],
              '&:hover': {
                backgroundColor: theme.palette.grey[50],
              },
            }}
          >
            <TableCell>
              {formatMessage({ id: 'adminProjects.reallocation.preview.batch' })}
            </TableCell>
            <TableCell>
              {formatMessage({ id: 'adminProjects.reallocation.preview.annotator' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.pendingBefore' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.deltaPending' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.pendingAfter' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.draft' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.finalized' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.totalBefore' })}
            </TableCell>
            <TableCell align="right">
              {formatMessage({ id: 'adminProjects.reallocation.preview.totalAfter' })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) =>
            group.rows.map((row, index) => {
              const changed = row.deltaPending !== 0;
              return (
                <TableRow
                  key={`${group.batchLabel}-${row.annotatorId}`}
                  sx={{
                    backgroundColor: changed ? theme.palette.action.hover : 'inherit',
                    '&:hover': {
                      backgroundColor: theme.palette.grey[50],
                    },
                  }}
                >
                  {index === 0 && (
                    <TableCell
                      rowSpan={group.rows.length}
                      sx={{ verticalAlign: 'top', fontWeight: 600 }}
                    >
                      {group.batchLabel}
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                      {row.isAiAgent && (
                        <SmartToyOutlinedIcon
                          fontSize="small"
                          sx={{ color: theme.palette.primary.main }}
                        />
                      )}
                      {row.annotatorName}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {row.pendingBefore.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <DeltaCell value={row.deltaPending} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {row.pendingAfter.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ color: theme.palette.text.secondary }}>
                    {row.draftCount.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ color: theme.palette.text.secondary }}>
                    {row.finalisedCount.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {row.totalBefore.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {row.totalAfter.toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Box>
  );
};
